import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PaymentsService } from 'src/app/payments/services/payments.service';
import { AppService } from '@services/app.service';
import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { bsConfig } from '@shared/constants/bs-config.constants';
import { addMonths, format, subMonths } from 'date-fns';
import { pl } from 'date-fns/locale';
import { BsModalRef } from 'ngx-bootstrap/modal';

@Component({
    selector: 'app-licensing-report-xlsx',
    standalone: false,
    templateUrl: './licensing-report-xlsx.component.html',
    styleUrl: './licensing-report-xlsx.component.scss',
})
export class LicensingReportXlsxComponent implements OnInit {
    monthControl = new FormControl();
    bsConfig = bsConfig;
    months: { label: string; value: string }[] = [];
    isLoading = false;

    constructor(private readonly paymentService: PaymentsService, private readonly appSerice: AppService, private modalRef: BsModalRef) {}

    ngOnInit(): void {
        this.generateMonthsList();
    }

    generate(): void {
        const monthValue = this.monthControl.value;
        if (!monthValue) {
            console.warn('Brak miesiąca');
            return;
        }

        this.isLoading = true;

        const [year, month] = monthValue.split('-').map(Number);
        const startDate = `${year}-${this.pad(month)}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const endDate = `${year}-${this.pad(month)}-${lastDay}`;

        Promise.all([this.paymentService.licensingDeliveries(startDate, endDate).toPromise(), this.paymentService.licensingHardware(startDate, endDate).toPromise(), this.paymentService.licensingUsers(endDate, '', '%frisco.pl').toPromise()]).then(async ([deliveriesMap, hardwareMap, users]) => {
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Frisco Invoicing');

            const titleRow = sheet.addRow(['Frisco Invoicing']);
            titleRow.font = { size: 14, bold: true, name: 'Arial' };
            sheet.mergeCells(`A${titleRow.number}:C${titleRow.number}`);
            titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCE5FF' } };
            sheet.addRow([]);

            const deliveryRate = 0.35;
            const hardwareRate = 79.0;
            const userRate = 199.0;
            const totalUsers = users.length;
            const sumCells: string[] = [];
            let totalDeliveries = 0;
            let totalHardware = 0;

            const sortedDepots = Object.entries(deliveriesMap)
                .map(([depotCode, rawDeliveries]) => {
                    const deliveries = Array.isArray(rawDeliveries) ? rawDeliveries : [];
                    const total = deliveries.reduce((sum, d) => sum + (d?.count || 0), 0);
                    return { depotCode, deliveries, total };
                })
                .sort((a, b) => b.total - a.total);

            for (const { depotCode, deliveries } of sortedDepots) {
                const depotDeliveries = deliveries.reduce((sum, d) => sum + (d?.count || 0), 0);
                totalDeliveries += depotDeliveries;

                const depotId = Object.keys(hardwareMap).find((key) => {
                    const ref = this.appSerice.findWarehouseRef(key);
                    return ref && ref === depotCode;
                });

                let totalDevices = 0;
                if (depotId && hardwareMap[depotId]) {
                    totalDevices = hardwareMap[depotId].length;
                }
                totalHardware += totalDevices;

                const addUsers = depotCode === 'WAW';
                let userFormulaRow: number | null = null;

                const header = sheet.addRow([`Faktura ${depotCode}`]);
                header.font = { bold: true, name: 'Arial', size: 10 };
                sheet.mergeCells(`A${header.number}:C${header.number}`);
                header.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };

                const r1 = sheet.addRow([`Wynagrodzenie za Dostawy ${depotCode}`, '', depotDeliveries]);
                r1.getCell(1).font = { name: 'Arial', size: 10 };

                const r2 = sheet.addRow(['', deliveryRate, null]);
                r2.getCell(2).numFmt = '#,##0.00';
                r2.getCell(3).value = { formula: `B${r2.number}*C${r2.number - 1}` };
                r2.getCell(3).numFmt = '#,##0.00 zł';
                r2.getCell(2).font = r2.getCell(3).font = { name: 'Arial', size: 10 };
                sumCells.push(`C${r2.number}`);

                const r3 = sheet.addRow([`Wynagrodzenie za Urządzenia ${depotCode}`, '', totalDevices]);
                r3.getCell(1).font = { name: 'Arial', size: 10 };

                const r4 = sheet.addRow(['', hardwareRate, null]);
                r4.getCell(2).numFmt = '#,##0.00';
                r4.getCell(3).value = { formula: `B${r4.number}*C${r4.number - 1}` };
                r4.getCell(3).numFmt = '#,##0.00 zł';
                r4.getCell(2).font = r4.getCell(3).font = { name: 'Arial', size: 10 };
                sumCells.push(`C${r4.number}`);

                if (addUsers) {
                    const r5 = sheet.addRow([`Wynagrodzenie za Konta Użytkowników`, '', totalUsers]);
                    r5.getCell(1).font = { name: 'Arial', size: 10 };

                    const r6 = sheet.addRow(['', userRate, null]);
                    r6.getCell(2).numFmt = '#,##0.00';
                    r6.getCell(3).value = { formula: `B${r6.number}*C${r6.number - 1}` };
                    r6.getCell(3).numFmt = '#,##0.00 zł';
                    r6.getCell(2).font = r6.getCell(3).font = { name: 'Arial', size: 10 };
                    sumCells.push(`C${r6.number}`);
                    userFormulaRow = r6.number;
                }

                const subtotalRow = sheet.addRow([`Suma ${depotCode}`, '', null]);
                subtotalRow.font = { bold: true, name: 'Arial', size: 10 };
                sheet.mergeCells(`A${subtotalRow.number}:C${subtotalRow.number}`);
                subtotalRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E5E5' } };

                const cellRefs = [r2.number, r4.number];
                if (userFormulaRow !== null) cellRefs.push(userFormulaRow);
                subtotalRow.getCell(3).value = { formula: `SUM(${cellRefs.map((n) => `C${n}`).join(',')})` };
                subtotalRow.getCell(3).numFmt = '#,##0.00 zł';

                sheet.addRow([]);
            }

            // Sprzęt bez przypisania do magazynu (-1)
            const unassignedDepotId = '-1';
            if (hardwareMap[unassignedDepotId]?.length) {
                const unassignedDevices = hardwareMap[unassignedDepotId].length;
                totalHardware += unassignedDevices;

                const header = sheet.addRow([`Brak przypisania do magazynu`]);
                header.font = { bold: true, name: 'Arial', size: 10 };
                sheet.mergeCells(`A${header.number}:C${header.number}`);
                header.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };

                const r1 = sheet.addRow([`Wynagrodzenie za Urządzenia (brak przypisania)`, '', unassignedDevices]);
                r1.getCell(1).font = { name: 'Arial', size: 10 };

                const r2 = sheet.addRow(['', hardwareRate, null]);
                r2.getCell(2).numFmt = '#,##0.00';
                r2.getCell(3).value = { formula: `B${r2.number}*C${r2.number - 1}` };
                r2.getCell(3).numFmt = '#,##0.00 zł';
                r2.getCell(2).font = r2.getCell(3).font = { name: 'Arial', size: 10 };
                sumCells.push(`C${r2.number}`);

                const subtotalRow = sheet.addRow([`Suma brak przypisania`, '', null]);
                subtotalRow.font = { bold: true, name: 'Arial', size: 10 };
                sheet.mergeCells(`A${subtotalRow.number}:C${subtotalRow.number}`);
                subtotalRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E5E5' } };
                subtotalRow.getCell(3).value = { formula: `C${r2.number}` };
                subtotalRow.getCell(3).numFmt = '#,##0.00 zł';

                sheet.addRow([]);
            }

            const totalRow = sheet.addRow(['TOTAL', '', null]);
            totalRow.font = { bold: true, name: 'Arial', size: 10 };
            totalRow.getCell(3).value = { formula: sumCells.length ? `SUM(${sumCells.join(',')})` : '0' };
            totalRow.getCell(3).numFmt = '#,##0.00 zł';

            sheet.columns.forEach((col) => {
                let maxLength = 10;
                col.eachCell?.((c) => {
                    maxLength = Math.max(maxLength, c.value ? c.value.toString().length : 0);
                });
                col.width = maxLength + 2;
            });

            // Dodanie sekcji checksums
            sheet.addRow([]);
            const checksumTitleRow = sheet.addRow(['Checksums']);
            sheet.mergeCells(`A${checksumTitleRow.number}:C${checksumTitleRow.number}`);
            checksumTitleRow.font = { bold: true, name: 'Arial', size: 10 };
            checksumTitleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };

            const checksum1 = sheet.addRow(['Total liczba dostaw z OR', '', totalDeliveries]);
            checksum1.getCell(1).font = { name: 'Arial', size: 10 };
            checksum1.getCell(3).numFmt = '0';

            const checksum2 = sheet.addRow(['Total liczba dostaw z pliku (formula sumy)', '', null]);
            checksum2.getCell(1).font = { name: 'Arial', size: 10 };
            const deliveryRows = sheet.getRows(1, sheet.rowCount)?.filter((row) => row.getCell(1).value?.toString().includes('Wynagrodzenie za Dostawy')) || [];
            const deliveryCellRefs = deliveryRows.map((row) => `C${row.number}`);
            checksum2.getCell(3).value = { formula: `SUM(${deliveryCellRefs.join(',')})` };
            checksum2.getCell(3).numFmt = '0';

            const checksum3 = sheet.addRow(['Czy suma dostaw zgodna?', '', null]);
            checksum3.getCell(2).value = { formula: `C${checksum1.number}=C${checksum2.number}` };
            checksum3.getCell(2).font = { name: 'Arial', size: 10 };

            const checksum4 = sheet.addRow(['Total liczba hardware z OR', '', totalHardware]);
            checksum4.getCell(1).font = { name: 'Arial', size: 10 };
            checksum4.getCell(3).numFmt = '0';

            const checksum5 = sheet.addRow(['Total liczba hardware z pliku (formula sumy)', '', null]);
            checksum5.getCell(1).font = { name: 'Arial', size: 10 };
            const hardwareRows = sheet.getRows(1, sheet.rowCount)?.filter((row) => row.getCell(1).value?.toString().includes('Wynagrodzenie za Urządzenia')) || [];
            const hardwareCellRefs = hardwareRows.map((row) => `C${row.number}`);
            checksum5.getCell(3).value = { formula: `SUM(${hardwareCellRefs.join(',')})` };
            checksum5.getCell(3).numFmt = '0';

            const checksum6 = sheet.addRow(['Czy hardware zgodny?', '', null]);
            checksum6.getCell(2).value = { formula: `C${checksum4.number}=C${checksum5.number}` };
            checksum6.getCell(2).font = { name: 'Arial', size: 10 };

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            FileSaver.saveAs(blob, `frisco-invoicing-${monthValue}.xlsx`);

            this.isLoading = false;
        });
    }

    generateMonthsList(): void {
        const today = new Date();
        const monthsToShow = 12 + 2; // 12 wstecz + 2 w przód

        this.months = Array.from({ length: monthsToShow }, (_, i) => {
            const monthDate = subMonths(today, 12 - i - 1); // od 12 wstecz do 2 w przód
            if (i >= 12) {
                const futureOffset = i - 11;
                return this.createMonthOption(addMonths(today, futureOffset));
            }
            return this.createMonthOption(monthDate);
        });

        // Domyślnie poprzedni miesiąc
        const prevMonth = format(subMonths(today, 1), 'yyyy-MM');
        this.monthControl.setValue(prevMonth);
    }

    createMonthOption(date: Date) {
        return {
            value: format(date, 'yyyy-MM'),
            label: format(date, 'LLLL yyyy', { locale: pl }),
        };
    }

    close() {
        this.modalRef.hide();
    }

    private pad(n: number): string {
        return n < 10 ? `0${n}` : `${n}`;
    }
}
